import type {
  ResolvedView,
  TabHostApi,
  TabHostControllerProps,
} from '@hibro/shared';
import {
  type FunctionComponent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useViewEventBus } from '../../event-bus/event-bus';
import { useTranslator } from '../../i18n/i18n';
import type { UiRegistry } from '../../runtime/ui-registry';

export interface TabControllerOutletProps {
  readonly host: TabHostApi;
  readonly views: readonly ResolvedView[];
  readonly registry: UiRegistry;
  readonly executeCommand: (id: string, ...args: unknown[]) => Promise<unknown>;
}

/**
 * Mounts the controller for the tabbed area's current view. With one
 * contributor it can seed an empty area; with several contributors, an
 * explicit open intent establishes the active view before any controller runs.
 */
export function TabControllerOutlet({
  host,
  views,
  registry,
  executeCommand,
}: TabControllerOutletProps): ReactNode {
  const translator = useTranslator();
  const subscribeEvent = useViewEventBus();
  const [, setVersion] = useState(0);
  const [, setActivationVersion] = useState(0);
  const candidates = useMemo(
    () => views.filter((view) => view.area === host.area),
    [host.area, views],
  );

  useEffect(
    () => host.subscribe(() => setVersion((version) => version + 1)),
    [host],
  );

  useEffect(() => {
    let active = true;
    void Promise.all(
      candidates.map((view) => registry.activateForModule(view.moduleId)),
    ).then(() => {
      if (active) {
        setActivationVersion((version) => version + 1);
      }
    });
    return () => {
      active = false;
    };
  }, [candidates, registry]);

  const tabs = host.getTabs();
  const selectedViewId =
    tabs.find((tab) => tab.active)?.viewId ??
    tabs[0]?.viewId ??
    (candidates.length === 1 ? candidates[0].id : undefined);
  const view = candidates.find((candidate) => candidate.id === selectedViewId);
  const Controller = selectedViewId
    ? (registry.getTabController(selectedViewId) as
        | FunctionComponent<TabHostControllerProps>
        | undefined)
    : undefined;

  const t = useCallback<TabHostControllerProps['t']>(
    (key, args) => (view ? translator.t(`${view.moduleId}:${key}`, args) : key),
    [translator, view],
  );
  const onEvent = useCallback<TabHostControllerProps['onEvent']>(
    (event, handler) => subscribeEvent(event, handler),
    [subscribeEvent],
  );

  if (!view || !Controller) {
    return null;
  }
  return (
    <Controller
      moduleId={view.moduleId}
      viewId={view.id}
      host={host}
      executeCommand={executeCommand}
      onEvent={onEvent}
      t={t}
    />
  );
}
